import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Button, Table, TableColumn } from '@/components/common';
import { useUIStore } from '@/store/uiStore';
import { cuttingApi } from '@/api/endpoints/cutting';
import { CuttingOrder, CuttingOrderLine, CutConfig, CuttingOrderStatus } from '@/types/cutting';

const CuttingOrderDetailModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal, selectedCuttingOrder } = useUIStore();
  const [loading, setLoading] = useState(false);
  const [cuttingOrder, setCuttingOrder] = useState<CuttingOrder | null>(null);
  const [cutConfigs, setCutConfigs] = useState<CutConfig[]>([]);

  const isOpen = modals.cuttingOrderDetail;

  // Fetch cutting order detail when modal opens
  useEffect(() => {
    if (isOpen && selectedCuttingOrder) {
      fetchCuttingOrderDetail();
    }
  }, [isOpen, selectedCuttingOrder]);

  const fetchCuttingOrderDetail = async () => {
    if (!selectedCuttingOrder) return;
    
    setLoading(true);
    try {
      const response = await cuttingApi.getCuttingOrderDetail(selectedCuttingOrder);
      setCuttingOrder(response.cuttingOrder);
      setCutConfigs(response.cutConfigs);
    } catch (error) {
      console.error('Error fetching cutting order detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('cuttingOrderDetail');
    setCuttingOrder(null);
    setCutConfigs([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const getStatusBadge = (status: CuttingOrderStatus) => {
    const statusConfig = {
      created: { color: 'bg-blue-100 text-blue-800', label: 'Đã tạo' },
      cut: { color: 'bg-yellow-100 text-yellow-800', label: 'Đã cắt' },
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Đã xác nhận' },
    };

    const config = statusConfig[status];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getCutTypeBadge = (cutType: string) => {
    const typeConfig = {
      cut_wood: { color: 'bg-brown-100 text-brown-800', label: 'Cắt gỗ' },
      cut_mica: { color: 'bg-blue-100 text-blue-800', label: 'Cắt mica' },
      cut_metal: { color: 'bg-gray-100 text-gray-800', label: 'Cắt kim loại' },
      cut_plastic: { color: 'bg-green-100 text-green-800', label: 'Cắt nhựa' },
      cut_glass: { color: 'bg-cyan-100 text-cyan-800', label: 'Cắt kính' },
    };

    const config = typeConfig[cutType as keyof typeof typeConfig] || typeConfig.cut_wood;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Cut lines table columns
  const cutLineColumns: TableColumn<CuttingOrderLine>[] = [
    {
      key: 'stt',
      title: 'STT',
      dataIndex: 'stt',
      width: 60,
      align: 'center',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          {value}
        </div>
      ),
    },
    {
      key: 'materialName',
      title: 'Tên nguyên liệu',
      dataIndex: 'materialName',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          {value}
        </div>
      ),
    },
    {
      key: 'cutType',
      title: 'Loại cắt',
      dataIndex: 'cutType',
      width: 120,
      render: (value) => getCutTypeBadge(value),
    },
    {
      key: 'quantity',
      title: 'Số lượng',
      dataIndex: 'quantity',
      width: 100,
      align: 'right',
      render: (value, record) => (
        <div className="text-sm font-medium text-gray-900">
          {value} {record.unit}
        </div>
      ),
    },
    {
      key: 'text',
      title: 'Ghi chú',
      dataIndex: 'text',
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value || '-'}
        </div>
      ),
    },
    {
      key: 'cutConfigId',
      title: 'Cấu hình',
      dataIndex: 'cutConfigId',
      width: 120,
      render: (value) => {
        if (!value) return <span className="text-gray-400">-</span>;
        
        const config = cutConfigs.find(c => c.id === value);
        return (
          <div className="text-xs">
            <div className="font-medium text-blue-600">{config?.name || value}</div>
            <div className="text-gray-500">{config?.materialType}</div>
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('tooltips.cutting.cuttingOrderDetail')}
      size="xl"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : cuttingOrder ? (
        <div className="space-y-6">
          {/* Header - Order Code + Status */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Lệnh cắt {cuttingOrder.code}</h2>
                <p className="text-sm text-gray-600">Ngày tạo: {formatDate(cuttingOrder.date)}</p>
              </div>
              <div>
                {getStatusBadge(cuttingOrder.status)}
              </div>
            </div>
          </div>

          {/* Order Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Người tạo</label>
                <p className="mt-1 text-sm text-gray-900">{cuttingOrder.createdBy}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Ngày cắt</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(cuttingOrder.date)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tổng số lượng</label>
                <p className="mt-1 text-sm font-medium text-gray-900">{cuttingOrder.totalQuantity} sản phẩm</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                <div className="mt-1">{getStatusBadge(cuttingOrder.status)}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Số cấu hình</label>
                <p className="mt-1 text-sm text-gray-900">{cuttingOrder.totalConfigs} mẫu</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Cập nhật cuối</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(cuttingOrder.updatedAt)}</p>
              </div>
            </div>

            {/* Full width fields */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Ghi chú</label>
              <p className="mt-1 text-sm text-gray-900">{cuttingOrder.notes || '-'}</p>
            </div>
          </div>

          {/* Cut Lines Table */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Danh sách cắt</h3>
            
            <div className="border border-gray-200 rounded-lg">
              <Table
                columns={cutLineColumns}
                data={cuttingOrder.cutLines}
                rowKey="id"
                size="sm"

                emptyText="Không có dòng cắt nào"
              />
            </div>
          </div>

          {/* Used Configurations */}
          {cutConfigs.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Cấu hình đã sử dụng</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cutConfigs.map((config) => (
                  <div key={config.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="font-medium text-gray-900">{config.name}</div>
                    <div className="text-sm text-gray-600">{config.materialType}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {config.cutConfigLines.length} dòng cắt • Đã dùng {config.usageCount} lần
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              variant="primary"
              onClick={handleClose}
            >
              Đóng
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">Không tìm thấy thông tin lệnh cắt</p>
        </div>
      )}
    </Modal>
  );
};

export default CuttingOrderDetailModal;
